import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { FilterX, ListFilter, Pause, Play, Radio, Search, Signal, X } from 'lucide-react'
import {
  getChannels,
  getAudioTrack,
  getListeningSignalSummary,
  listeningSignalTypes,
  type Channel,
  type ListeningSignalGroupBy,
  type ListeningSignalSummary,
  type ListeningSignalSummaryItem,
  type ListeningSignalType
} from '../api'
import { useToast } from '../components/ToastProvider'
import { Button, DataTableElement, DataTableSurface, EmptyState, IconButton, MobileEntityList, MobileEntityRow, Pill, SearchField, TablePagination } from '../components/ui'
import { useAudioPlayerContext } from '../features/audio/AudioPlayerProvider'
import type { AdminOutletContext } from '../layouts/AdminLayout'
import styles from './ListeningSignalsPage.module.css'

type Filters = {
  channelId: string
  fromDate: string
  groupBy: ListeningSignalGroupBy
  signalType: '' | ListeningSignalType
  toDate: string
}

const signalLabels: Record<ListeningSignalType, string> = {
  bad_audio_quality: 'Bad audio',
  neutral_audio_quality: 'Neutral audio',
  good_audio_quality: 'Good audio',
  boring_track: 'Boring',
  neutral_track: 'Neutral track',
  great_track: 'Great track',
  poor_channel_fit: 'Poor fit',
  neutral_channel_fit: 'Neutral fit',
  great_channel_fit: 'Great fit'
}

const groupByLabels: Record<ListeningSignalGroupBy, string> = {
  channel: 'Channels',
  prompt: 'Prompts',
  track: 'Tracks'
}

type SignalMetric = {
  className: string
  getValue: (item: ListeningSignalSummaryItem) => string
  label: string
  shortLabel: string
}

type SignalMetricGroup = {
  key: string
  label: string
  metrics: SignalMetric[]
}

const sentimentMetrics: SignalMetric[] = [
  {
    className: 'signals-metric-positive',
    getValue: (item) => item.positive_count,
    label: 'Positive',
    shortLabel: 'Pos'
  },
  {
    className: 'signals-metric-neutral',
    getValue: (item) => item.neutral_count,
    label: 'Neutral',
    shortLabel: 'Neu'
  },
  {
    className: 'signals-metric-negative',
    getValue: (item) => item.negative_count,
    label: 'Negative',
    shortLabel: 'Neg'
  }
]

const signalMetricGroups: SignalMetricGroup[] = [
  {
    key: 'audio',
    label: 'Audio quality',
    metrics: [
      {
        className: 'signals-metric-negative',
        getValue: (item) => item.bad_audio_quality_count,
        label: 'Bad',
        shortLabel: 'B'
      },
      {
        className: 'signals-metric-neutral',
        getValue: (item) => item.neutral_audio_quality_count,
        label: 'Neutral',
        shortLabel: 'N'
      },
      {
        className: 'signals-metric-positive',
        getValue: (item) => item.good_audio_quality_count,
        label: 'Good',
        shortLabel: 'G'
      }
    ]
  },
  {
    key: 'track',
    label: 'Track appeal',
    metrics: [
      {
        className: 'signals-metric-negative',
        getValue: (item) => item.boring_track_count,
        label: 'Boring',
        shortLabel: 'B'
      },
      {
        className: 'signals-metric-neutral',
        getValue: (item) => item.neutral_track_count,
        label: 'Neutral',
        shortLabel: 'N'
      },
      {
        className: 'signals-metric-positive',
        getValue: (item) => item.great_track_count,
        label: 'Great',
        shortLabel: 'G'
      }
    ]
  },
  {
    key: 'fit',
    label: 'Channel fit',
    metrics: [
      {
        className: 'signals-metric-negative',
        getValue: (item) => item.poor_channel_fit_count,
        label: 'Poor',
        shortLabel: 'P'
      },
      {
        className: 'signals-metric-neutral',
        getValue: (item) => item.neutral_channel_fit_count,
        label: 'Neutral',
        shortLabel: 'N'
      },
      {
        className: 'signals-metric-positive',
        getValue: (item) => item.great_channel_fit_count,
        label: 'Great',
        shortLabel: 'G'
      }
    ]
  }
]

const emptyFilters: Filters = {
  channelId: '',
  fromDate: '',
  groupBy: 'track',
  signalType: '',
  toDate: ''
}

const mobileSignalsQuery = '(max-width: 900px)'

const cx = (...classNames: Array<string | false | null | undefined>): string =>
  classNames
    .filter(Boolean)
    .map((className) => styles[className as string] ?? className)
    .join(' ')

const dateToIso = (date: string, endExclusive: boolean): string | undefined => {
  if (!date) {
    return undefined
  }

  const value = new Date(`${date}T00:00:00`)

  if (endExclusive) {
    value.setDate(value.getDate() + 1)
  }

  return value.toISOString()
}

const formatCount = (value: string): number => Number(value || 0)

const getSummaryItemTitle = (
  item: ListeningSignalSummaryItem,
  groupBy: ListeningSignalGroupBy
): string => (groupBy === 'track' ? item.track_title || item.target_name : item.target_name)

export const ListeningSignalsPage = () => {
  const { toast } = useToast()
  const { setMobileMenuItems } = useOutletContext<AdminOutletContext>()
  const {
    audioLoading,
    audioPlaying,
    playingTrack,
    playTrack,
    registerPlayNext
  } = useAudioPlayerContext()
  const [channels, setChannels] = useState<Channel[]>([])
  const [filters, setFilters] = useState<Filters>(emptyFilters)
  const [page, setPage] = useState(1)
  const [summary, setSummary] = useState<ListeningSignalSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingTrackId, setLoadingTrackId] = useState<string | null>(null)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [isMobileSignalsPage, setIsMobileSignalsPage] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(mobileSignalsQuery).matches
  )

  useEffect(() => {
    setMobileMenuItems(null)
  }, [setMobileMenuItems])

  useEffect(() => {
    const mediaQuery = window.matchMedia(mobileSignalsQuery)
    const syncMobileSignalsPage = (): void => setIsMobileSignalsPage(mediaQuery.matches)

    syncMobileSignalsPage()
    mediaQuery.addEventListener('change', syncMobileSignalsPage)

    return () => mediaQuery.removeEventListener('change', syncMobileSignalsPage)
  }, [])

  useEffect(() => {
    void getChannels().then(setChannels).catch((error) => {
      toast(error instanceof Error ? error.message : 'Unable to load channels.')
    })
  }, [toast])

  const hasFilters = Boolean(filters.channelId || filters.fromDate || filters.toDate || filters.signalType || filters.groupBy !== 'track')

  const loadSummary = useCallback(async (): Promise<void> => {
    setLoading(true)
    try {
      const payload = await getListeningSignalSummary({
        channelId: filters.channelId || undefined,
        from: dateToIso(filters.fromDate, false),
        groupBy: filters.groupBy,
        page,
        pageSize: 25,
        signalType: filters.signalType || undefined,
        to: dateToIso(filters.toDate, true)
      })
      setSummary(payload)
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Unable to load listening signals.')
    } finally {
      setLoading(false)
    }
  }, [filters, page, toast])

  useEffect(() => {
    void loadSummary()
  }, [loadSummary])

  const totals = useMemo(() => {
    const items = summary?.items ?? []

    return items.reduce(
      (acc, item) => ({
        negative: acc.negative + formatCount(item.negative_count),
        neutral: acc.neutral + formatCount(item.neutral_count),
        positive: acc.positive + formatCount(item.positive_count),
        total: acc.total + formatCount(item.total_count)
      }),
      { negative: 0, neutral: 0, positive: 0, total: 0 }
    )
  }, [summary])
  const totalPages = summary ? Math.max(1, Math.ceil(summary.total / summary.pageSize)) : 1
  const firstItemNumber = summary && summary.total > 0 ? ((page - 1) * summary.pageSize) + 1 : 0
  const lastItemNumber = summary ? Math.min(page * summary.pageSize, summary.total) : 0
  const paginationSummary = summary?.total === 0 ? 'No signals' : `${firstItemNumber}-${lastItemNumber} of ${summary?.total ?? 0}`
  const visibleMobileItems = useMemo(() => {
    const items = summary?.items ?? []
    const query = searchInput.trim().toLowerCase()

    if (!query) {
      return items
    }

    return items.filter((item) => [
      item.target_name,
      item.track_artist ?? '',
      filters.groupBy === 'channel' ? 'Live channel feedback' : '',
      filters.groupBy === 'prompt' && !item.target_id ? 'Tracks without a captured prompt binding' : ''
    ].some((value) => value.toLowerCase().includes(query)))
  }, [filters.groupBy, searchInput, summary?.items])

  const applyFilter = (patch: Partial<Filters>): void => {
    setPage(1)
    setFilters((current) => ({ ...current, ...patch }))
  }

  const clearFilters = (): void => {
    setPage(1)
    setFilters(emptyFilters)
  }

  const submitSearch = (event?: FormEvent<HTMLFormElement>): void => {
    event?.preventDefault()
  }

  const activeFilterCount = (filters.channelId ? 1 : 0)
    + (filters.fromDate ? 1 : 0)
    + (filters.toDate ? 1 : 0)
    + (filters.signalType ? 1 : 0)
    + (filters.groupBy !== 'track' ? 1 : 0)

  const totalMetrics = [
    {
      className: 'signals-stat-total',
      label: 'Total',
      value: totals.total
    },
    {
      className: 'signals-stat-positive',
      label: 'Positive',
      value: totals.positive
    },
    {
      className: 'signals-stat-neutral',
      label: 'Neutral',
      value: totals.neutral
    },
    {
      className: 'signals-stat-negative',
      label: 'Negative',
      value: totals.negative
    }
  ]

  const renderMetricGrid = (
    metrics: SignalMetric[],
    item: ListeningSignalSummaryItem,
    label: string,
    compact = false
  ) => (
    <span className={cx('signals-metric-grid', compact && 'signals-metric-grid-compact')} aria-label={`${label} counts for ${item.target_name}`}>
      {metrics.map((metric) => {
        const value = metric.getValue(item)

        return (
          <span
            className={cx('signals-metric', metric.className, formatCount(value) === 0 && 'signals-metric-zero')}
            key={`${label}-${metric.label}`}
          >
            <span>{compact ? metric.shortLabel : metric.label}</span>
            <strong>{value}</strong>
          </span>
        )
      })}
    </span>
  )

  const renderPagination = (className?: string) => {
    const classNameProps = className ? { className } : {}

    return summary ? (
      <TablePagination
        ariaLabel="Listening signal pagination"
        currentPage={page}
        onNext={() => setPage((value) => Math.min(totalPages, value + 1))}
        onPrevious={() => setPage((value) => Math.max(1, value - 1))}
        summary={paginationSummary}
        totalPages={totalPages}
        {...classNameProps}
      />
    ) : null
  }

  const playSummaryTrack = useCallback(async (trackId: string): Promise<void> => {
    setLoadingTrackId(trackId)
    try {
      const track = await getAudioTrack(trackId)
      playTrack(track)
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Unable to play track.')
    } finally {
      setLoadingTrackId(null)
    }
  }, [playTrack, toast])

  useEffect(() => {
    registerPlayNext(() => {
      const rows = summary?.items.filter((item) => item.track_id) ?? []

      if (rows.length === 0 || !playingTrack) {
        return
      }

      const currentIndex = rows.findIndex((item) => item.track_id === playingTrack.id)
      const next = rows[(currentIndex + 1) % rows.length]

      if (next?.track_id) {
        void playSummaryTrack(next.track_id)
      }
    })

    return () => registerPlayNext(null)
  }, [playSummaryTrack, playingTrack, registerPlayNext, summary?.items])

  return (
    <div className={cx('admin-listening-signals', 'signals-page', playingTrack && 'admin-listening-signals-player', playingTrack && 'signals-page-player')}>
      {!isMobileSignalsPage && (
        <div className={cx('page-header', 'signals-header')}>
          <div>
            <h1 className="page-title">Listening Signals</h1>
            <p className="page-subtitle">Live listening feedback aggregated by track, prompt, and channel.</p>
          </div>
        </div>
      )}

      <div className={cx('signals-layout')}>
        <form className={cx('signals-mobile-toolbar')} onSubmit={submitSearch}>
          <SearchField
            aria-label="Search listening signals"
            className={cx('signals-mobile-search')}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search signals..."
            size="mobile"
            value={searchInput}
          />
          <button
            aria-label="Search listening signals"
            className={cx('signals-mobile-icon-button')}
            type="submit"
          >
            <Search size={17} />
          </button>
          <button
            aria-label={activeFilterCount > 0 ? `Open filters, ${activeFilterCount} active` : 'Open filters'}
            className={cx('signals-mobile-icon-button', activeFilterCount > 0 && 'signals-mobile-icon-button-active')}
            onClick={() => setMobileFiltersOpen(true)}
            title="Filters"
            type="button"
          >
            <ListFilter size={17} />
            {activeFilterCount > 0 && <span className={cx('signals-mobile-filter-count')}>{activeFilterCount}</span>}
          </button>
        </form>

        <section className={cx('signals-stats')} aria-label="Signal totals">
          <div className={cx('signals-stats-strip')}>
            {totalMetrics.map((metric) => (
              <div className={cx('signals-stat', metric.className)} key={metric.label}>
                <span>{metric.label}</span>
                <strong>{metric.value}</strong>
              </div>
            ))}
          </div>
        </section>

      <section className={cx('signals-filters')} aria-label="Listening signal filters">
        <label>
          <span>Group by</span>
          <select
            className="form-input select-input"
            onChange={(event) => applyFilter({ groupBy: event.target.value as ListeningSignalGroupBy })}
            value={filters.groupBy}
          >
            {(['track', 'prompt', 'channel'] as ListeningSignalGroupBy[]).map((groupBy) => (
              <option key={groupBy} value={groupBy}>{groupByLabels[groupBy]}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Channel</span>
          <select
            className="form-input select-input"
            onChange={(event) => applyFilter({ channelId: event.target.value })}
            value={filters.channelId}
          >
            <option value="">All channels</option>
            {channels.map((channel) => (
              <option key={channel.id} value={channel.id}>{channel.name}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Signal</span>
          <select
            className="form-input select-input"
            onChange={(event) => applyFilter({ signalType: event.target.value as Filters['signalType'] })}
            value={filters.signalType}
          >
            <option value="">All signals</option>
            {listeningSignalTypes.map((signalType) => (
              <option key={signalType} value={signalType}>{signalLabels[signalType]}</option>
            ))}
          </select>
        </label>
        <label>
          <span>From</span>
          <input
            className="form-input"
            onChange={(event) => applyFilter({ fromDate: event.target.value })}
            type="date"
            value={filters.fromDate}
          />
        </label>
        <label>
          <span>To</span>
          <input
            className="form-input"
            onChange={(event) => applyFilter({ toDate: event.target.value })}
            type="date"
            value={filters.toDate}
          />
        </label>
        {hasFilters && (
          <Button className={cx('signals-clear')} onClick={clearFilters}>
            <FilterX size={16} /> Clear
          </Button>
        )}
      </section>

      {mobileFiltersOpen && (
        <div
          className={cx('signals-mobile-filter-backdrop')}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setMobileFiltersOpen(false)
            }
          }}
        >
          <section className={cx('signals-mobile-filter-panel')} role="dialog" aria-modal="true" aria-label="Listening signal filters">
            <div className={cx('signals-mobile-filter-header')}>
              <div>
                <span>Filters</span>
                <strong>Listening Signals</strong>
              </div>
              <button
                aria-label="Close filters"
                className={cx('signals-mobile-filter-close')}
                onClick={() => setMobileFiltersOpen(false)}
                type="button"
              >
                <X size={18} />
              </button>
            </div>
            <div className={cx('signals-mobile-filter-body')}>
              <label>
                <span>Group by</span>
                <select
                  className="form-input select-input"
                  onChange={(event) => applyFilter({ groupBy: event.target.value as ListeningSignalGroupBy })}
                  value={filters.groupBy}
                >
                  {(['track', 'prompt', 'channel'] as ListeningSignalGroupBy[]).map((groupBy) => (
                    <option key={groupBy} value={groupBy}>{groupByLabels[groupBy]}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Channel</span>
                <select
                  className="form-input select-input"
                  onChange={(event) => applyFilter({ channelId: event.target.value })}
                  value={filters.channelId}
                >
                  <option value="">All channels</option>
                  {channels.map((channel) => (
                    <option key={channel.id} value={channel.id}>{channel.name}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Signal</span>
                <select
                  className="form-input select-input"
                  onChange={(event) => applyFilter({ signalType: event.target.value as Filters['signalType'] })}
                  value={filters.signalType}
                >
                  <option value="">All signals</option>
                  {listeningSignalTypes.map((signalType) => (
                    <option key={signalType} value={signalType}>{signalLabels[signalType]}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>From</span>
                <input
                  className="form-input"
                  onChange={(event) => applyFilter({ fromDate: event.target.value })}
                  type="date"
                  value={filters.fromDate}
                />
              </label>
              <label>
                <span>To</span>
                <input
                  className="form-input"
                  onChange={(event) => applyFilter({ toDate: event.target.value })}
                  type="date"
                  value={filters.toDate}
                />
              </label>
            </div>
            <div className={cx('signals-mobile-filter-actions')}>
              <Button disabled={!hasFilters} onClick={clearFilters}>
                Clear
              </Button>
              <Button onClick={() => setMobileFiltersOpen(false)} variant="primary">
                Apply{activeFilterCount > 0 ? ` ${activeFilterCount}` : ''}
              </Button>
            </div>
          </section>
        </div>
      )}

      <DataTableSurface className={cx('signals-table')} fullWidth>
        <div className={cx('signals-table-scroll')}>
          <DataTableElement>
            <thead>
              <tr>
                <th>{groupByLabels[filters.groupBy].slice(0, -1)}</th>
                <th>Total</th>
                <th>Overall</th>
                {signalMetricGroups.map((group) => (
                  <th key={group.key}>{group.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {summary?.items.map((item) => {
                const title = getSummaryItemTitle(item, filters.groupBy)

                return (
                  <tr key={`${item.target_id ?? 'none'}-${item.target_name}`}>
                    <td className={cx('signals-target-cell')} data-label={groupByLabels[filters.groupBy].slice(0, -1)}>
                      <div className={cx('signals-target-inner')}>
                        {item.track_id ? (
                          <IconButton
                            className={cx('signals-track-play')}
                            disabled={loadingTrackId === item.track_id || (audioLoading && playingTrack?.id === item.track_id)}
                            icon={playingTrack?.id === item.track_id && audioPlaying ? <Pause size={15} /> : <Play size={15} />}
                            label={`Preview ${item.target_name}`}
                            onClick={() => void playSummaryTrack(item.track_id!)}
                            title={playingTrack?.id === item.track_id && audioPlaying ? 'Playing' : 'Preview track'}
                          />
                        ) : (
                          <span className={cx('signals-track-play-spacer')} aria-hidden="true" />
                        )}
                        <span className={cx('signals-target-copy')}>
                          <strong>{title}</strong>
                          {filters.groupBy === 'track' && item.track_artist && <span>{item.track_artist}</span>}
                          {filters.groupBy === 'prompt' && !item.target_id && <span>Tracks without a captured prompt binding</span>}
                          {filters.groupBy === 'channel' && <span>Live channel feedback</span>}
                        </span>
                      </div>
                    </td>
                    <td data-label="Total"><Pill strong>{item.total_count}</Pill></td>
                  <td className={cx('signals-group-cell')} data-label="Overall">
                    {renderMetricGrid(sentimentMetrics, item, 'Overall')}
                    </td>
                    {signalMetricGroups.map((group) => (
                      <td className={cx('signals-group-cell')} data-label={group.label} key={group.key}>
                        {renderMetricGrid(group.metrics, item, group.label)}
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </DataTableElement>
          {!loading && summary?.items.length === 0 && (
            <EmptyState>
              <Signal size={24} />
              <span>No listening signals match these filters.</span>
            </EmptyState>
          )}
          {loading && (
            <EmptyState>
              <Radio size={24} />
              <span>Loading listening signals...</span>
            </EmptyState>
          )}
        </div>
        {renderPagination(cx('signals-desktop-pagination'))}
      </DataTableSurface>

      <MobileEntityList
        className={cx('signals-mobile-list')}
        empty={(
          <>
            {loading && (
              <EmptyState>
                <Radio size={24} />
                <span>Loading listening signals...</span>
              </EmptyState>
            )}
            {!loading && summary?.items.length === 0 && (
              <EmptyState>
                <Signal size={24} />
                <span>No listening signals match these filters.</span>
              </EmptyState>
            )}
            {!loading && summary && summary.items.length > 0 && visibleMobileItems.length === 0 && (
              <EmptyState>
                <Signal size={24} />
                <span>No listening signals match this search.</span>
              </EmptyState>
            )}
          </>
        )}
      >
        {visibleMobileItems.map((item) => {
          const title = getSummaryItemTitle(item, filters.groupBy)

          return (
            <MobileEntityRow
              className={cx('signals-mobile-row')}
              key={`mobile-${item.target_id ?? 'none'}-${item.target_name}`}
              leading={item.track_id ? (
                <IconButton
                  disabled={loadingTrackId === item.track_id || (audioLoading && playingTrack?.id === item.track_id)}
                  icon={playingTrack?.id === item.track_id && audioPlaying ? <Pause size={15} /> : <Play size={15} />}
                  iconSize="play"
                  label={`Preview ${item.target_name}`}
                  onClick={() => void playSummaryTrack(item.track_id!)}
                  title={playingTrack?.id === item.track_id && audioPlaying ? 'Playing' : 'Preview track'}
                />
              ) : (
                <span className={cx('signals-mobile-placeholder')} aria-hidden="true" />
              )}
              subtitle={(
                <>
                  <span className={cx('signals-mobile-subtitle')}>
                    {filters.groupBy === 'track' && item.track_artist ? item.track_artist : null}
                    {filters.groupBy === 'prompt' && !item.target_id ? 'Tracks without a captured prompt binding' : null}
                    {filters.groupBy === 'channel' ? 'Live channel feedback' : null}
                  </span>
                  <span className={cx('signals-mobile-metrics')} aria-label={`Signal counts for ${item.target_name}`}>
                    <span className={cx('signals-mobile-primary-metrics')}>
                      <span className={cx('signals-mobile-total')}>
                        <span>Total</span>
                        <strong>{item.total_count}</strong>
                      </span>
                    {renderMetricGrid(sentimentMetrics, item, 'Overall', true)}
                    </span>
                    <span className={cx('signals-mobile-signal-groups')}>
                      {signalMetricGroups.map((group) => (
                        <span className={cx('signals-mobile-signal-group')} key={group.key}>
                          <span>{group.label}</span>
                          {renderMetricGrid(group.metrics, item, group.label, true)}
                        </span>
                      ))}
                    </span>
                  </span>
                </>
              )}
              title={title}
            />
          )
        })}
      </MobileEntityList>
      </div>

      {summary && (
        <div className={cx('admin-listening-signals-pagination', 'signals-pagination')}>
          {renderPagination()}
        </div>
      )}
    </div>
  )
}
